import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";

const GenerateSalary = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [formData, setFormData] = useState({
    department: '',
    employee: '',
    payrollMonth: '',
    totalWorkingDays: 0,
    totalHalfDays: 0,
    totalLeaves: 0,
    deductions: {
      tax: 0,
      providentFund: 0,
      other: 0,
    },
    incomes: {
      bonus: 0,
      incentives: 0,
      other: 0,
    },
  });

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchCompanyData();
    fetchPayrollHistory();
    setLoading(false);
  }, []);

  // Update filtered employees when employees or department selection changes
  useEffect(() => {
    if (formData.department) {
      const filtered = employees.filter(employee => {
        const empDepartment = employee.department?._id || employee.department;
        return empDepartment === formData.department;
      });
      setFilteredEmployees(filtered);
      console.log('🏢 Filtered employees for department:', formData.department, filtered);
    } else {
      setFilteredEmployees(employees);
    }
  }, [employees, formData.department]);

  // 🎯 FETCH COMPANY DATA FOR WEEKLY HOLIDAYS
  const fetchCompanyData = async () => {
    const token = localStorage.getItem('authToken');
    let companyId = localStorage.getItem('selectedCompanyId');
    
    if (!companyId) {
      try {
        const decoded = jwtDecode(token);
        companyId = decoded.companyId || decoded.id;
      } catch (e) {
        console.error('Error decoding token:', e);
        return;
      }
    }

    if (!companyId) return;

    try {
      const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setSelectedCompany(response.data);
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  // 🏢 FETCH DEPARTMENTS FUNCTIONALITY
  const fetchDepartments = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('🔑 No access token found for departments fetch');
      return;
    }

    try {
      console.log('🚀 FETCHING DEPARTMENTS...');
      
      // Decode JWT token to get company ID
      const decoded = jwtDecode(token);
      console.log('🔍 DEPARTMENTS TOKEN DECODING:', {
        fullToken: decoded,
        companyId: decoded.companyId,
        id: decoded.id,
        orgId: decoded.orgId
      });

      // Extract company ID with multiple fallbacks
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
      console.log('🏢 EXTRACTED COMPANY ID FOR DEPARTMENTS:', companyId);

      if (!companyId) {
        console.error('❌ Company ID not found in token for departments');
        return;
      }

      const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/departments/company/${companyId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000,
        validateStatus: (status) => status < 500
      });

      console.log('📡 DEPARTMENTS API RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      if (response.status === 401) {
        console.error('🚫 Session expired during departments fetch');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }

      // Handle different response formats
      let departmentData = [];
      if (Array.isArray(response.data)) {
        departmentData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        departmentData = response.data.data;
      } else if (response.data?.departments && Array.isArray(response.data.departments)) {
        departmentData = response.data.departments;
      }

      console.log('🎯 PROCESSED DEPARTMENTS:', departmentData);

      // Log each department details
      departmentData.forEach((dept, index) => {
        console.log(`📋 Department ${index + 1}:`, {
          id: dept._id,
          name: dept.departmentName || dept.name,
          description: dept.description,
          company: dept.company,
          active: dept.active,
          createdAt: dept.createdAt
        });
      });

      setDepartments(departmentData);
      console.log(`✅ Successfully loaded ${departmentData.length} departments`);

    } catch (error) {
      console.error('❌ Error fetching departments:', error);
      
      if (error.response?.status === 401) {
        console.error('🚫 Authentication failed - redirecting to login');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
        console.error('🔒 Access denied for departments');
      } else if (error.response?.status === 404) {
        console.log('📭 No departments found for this company');
        setDepartments([]);
      } else {
        console.error('🌐 Network or server error:', error.message);
      }
    }
  };

  // 📊 FETCH PAYROLL HISTORY FUNCTIONALITY
  const fetchPayrollHistory = async () => {
    setHistoryLoading(true);
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('🔑 No access token found for payroll history fetch');
      setHistoryLoading(false);
      return;
    }

    try {
      console.log('🚀 FETCHING PAYROLL HISTORY...');
      
      // Decode JWT token to get company ID
      const decoded = jwtDecode(token);
      console.log('🔍 PAYROLL HISTORY TOKEN DECODING:', {
        fullToken: decoded,
        companyId: decoded.companyId,
        id: decoded.id,
        orgId: decoded.orgId
      });

      // Extract company ID with multiple fallbacks
      const companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
      console.log('🏢 EXTRACTED COMPANY ID FOR PAYROLL HISTORY:', companyId);

      if (!companyId) {
        console.error('❌ Company ID not found in token for payroll history');
        setHistoryLoading(false);
        return;
      }

      const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/payroll/company/${companyId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 15000,
        validateStatus: (status) => status < 500
      });

      console.log('📡 PAYROLL HISTORY API RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      if (response.status === 401) {
        console.error('🚫 Session expired during payroll history fetch');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }

      // Handle different response formats
      let payrollData = [];
      if (Array.isArray(response.data)) {
        payrollData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        payrollData = response.data.data;
      } else if (response.data?.payrolls && Array.isArray(response.data.payrolls)) {
        payrollData = response.data.payrolls;
      }

      console.log('🎯 PROCESSED PAYROLL HISTORY:', payrollData);

      // Log each payroll details
      payrollData.forEach((payroll, index) => {
        console.log(`💰 Payroll ${index + 1}:`, {
          id: payroll._id,
          employee: payroll.employee?.teamMemberName || payroll.employee?.name || payroll.employeeId,
          employeeId: payroll.employee?.employeeId || payroll.employeeId,
          department: payroll.employee?.department?.departmentName || payroll.employee?.department?.name,
          payrollMonth: payroll.payrollMonth,
          totalWorkingDays: payroll.totalWorkingDays,
          totalHalfDays: payroll.totalHalfDays,
          totalLeaves: payroll.totalLeaves,
          grossSalary: payroll.grossSalary,
          netSalary: payroll.netSalary,
          totalDeductions: payroll.totalDeductions,
          totalIncomes: payroll.totalIncomes,
          createdAt: payroll.createdAt,
          status: payroll.status
        });
      });

      // Sort by creation date (newest first)
      const sortedPayrolls = payrollData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setPayrollHistory(sortedPayrolls);
      console.log(`✅ Successfully loaded ${sortedPayrolls.length} payroll records`);

    } catch (error) {
      console.error('❌ Error fetching payroll history:', error);
      
      if (error.response?.status === 401) {
        console.error('🚫 Authentication failed - redirecting to login');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      } else if (error.response?.status === 403) {
        console.error('🔒 Access denied for payroll history');
      } else if (error.response?.status === 404) {
        console.log('📭 No payroll history found for this company');
        setPayrollHistory([]);
      } else {
        console.error('🌐 Network or server error:', error.message);
        setError('Failed to fetch payroll history. Please try again.');
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchEmployees = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(false);
      return;
    }

    try {
      // Get company ID from localStorage (set during login/company selection)
      let companyId = localStorage.getItem('selectedCompanyId');
      
      // If not in localStorage, try to extract from JWT token
      if (!companyId) {
        try {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          companyId = decoded.companyId || decoded.id;
        } catch (decodeError) {
          console.error('Error decoding token:', decodeError);
        }
      }
      
      if (!companyId) {
        setError('Company information not found. Please contact administrator.');
        setLoading(false);
        return;
      }

      // Fetch employees by company ID
      const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/employees/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setEmployees(response.data);
    } catch (err) {
      console.error("Error fetching employees:", err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else {
        setError('Failed to fetch company employees.');
      }
    }
  };

  // 🎯 FETCH COMPANY HOLIDAYS FUNCTIONALITY
  const fetchHolidays = async (companyId) => {
    const token = localStorage.getItem('authToken');
    if (!token || !companyId) return [];

    try {
      const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/holidays/company/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
        validateStatus: (status) => status < 500
      });

      let holidayData = [];
      if (Array.isArray(response.data)) {
        holidayData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        holidayData = response.data.data;
      } else if (response.data?.holidays && Array.isArray(response.data.holidays)) {
        holidayData = response.data.holidays;
      }

      return holidayData
        .filter(holiday => holiday && (holiday.date || holiday.holidayDate))
        .map(holiday => ({
          _id: holiday._id || holiday.id,
          name: holiday.name || holiday.holidayName || holiday.title || 'Holiday',
          date: new Date(holiday.date || holiday.holidayDate).toISOString().split('T')[0],
          type: holiday.type || 'General',
          description: holiday.description || ''
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    } catch (error) {
      console.error('Error fetching holidays:', error);
      return [];
    }
  };

  // 🎯 FETCH ATTENDANCE RECORDS FOR EMPLOYEE
  const fetchEmployeeAttendance = async (employeeId, startDate, endDate) => {
    const token = localStorage.getItem('authToken');
    if (!token || !employeeId) return [];

    try {
      const response = await axios.get(`https://customersupport-jhy9.onrender.com/api/attendance`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 15000,
        validateStatus: (status) => status < 500
      });

      let attendanceRecords = [];
      if (Array.isArray(response.data)) {
        attendanceRecords = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        attendanceRecords = response.data.data;
      } else if (response.data?.records && Array.isArray(response.data.records)) {
        attendanceRecords = response.data.records;
      }

      // Filter records for the specific employee and date range
      const filteredRecords = attendanceRecords
        .filter(record => {
          if (!record) return false;
          
          // Check employee match
          const recordEmployeeId = record.employee?._id || record.employee;
          if (recordEmployeeId !== employeeId) return false;
          
          // Check date range
          const recordDate = record.date ? new Date(record.date).toISOString().split('T')[0] : null;
          if (!recordDate) return false;
          
          return recordDate >= startDate && recordDate <= endDate;
        })
        .map(record => {
          // Calculate work hours if not present
          let calculatedHours = record.hours || '0:00';
          let workingTimeMinutes = record.workingTime || 0;
          
          // If we have inTime and outTime but no hours, calculate it
          if (record.inTime && record.outTime && (!record.hours || record.hours === '0:00')) {
            try {
              const inTime = new Date(record.inTime);
              const outTime = new Date(record.outTime);
              const diffMs = outTime - inTime;
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
              calculatedHours = `${diffHours}:${diffMinutes.toString().padStart(2, '0')}`;
              workingTimeMinutes = Math.floor(diffMs / (1000 * 60));
            } catch (e) {
              console.log('⚠️ Error calculating work hours for record:', record._id);
            }
          }
          
          return {
            _id: record._id,
            date: new Date(record.date).toISOString().split('T')[0],
            status: record.status || 'Present',
            inTime: record.inTime,
            outTime: record.outTime,
            workingTime: workingTimeMinutes,
            hours: calculatedHours
          };
        });
      
      console.log(`📊 Filtered ${filteredRecords.length} attendance records for employee ${employeeId}:`, filteredRecords);
      return filteredRecords;
    } catch (error) {
      console.error('Error fetching attendance:', error);
      return [];
    }
  };

  // 🎯 CALCULATE ATTENDANCE SUMMARY (Similar to DailyAttendance.js)
  const calculateAttendanceSummary = async (employeeId, payrollMonth) => {
    if (!employeeId || !payrollMonth) {
      return { totalWorkingDays: 0, totalHalfDays: 0, totalLeaves: 0 };
    }

    setAttendanceLoading(true);
    
    try {
      // Parse month (YYYY-MM format)
      const [year, month] = payrollMonth.split('-').map(Number);
      const monthIndex = month - 1; // JavaScript months are 0-indexed
      
      // Get date range for the month
      const startDate = new Date(year, monthIndex, 1).toISOString().split('T')[0];
      const endDate = new Date(year, monthIndex + 1, 0).toISOString().split('T')[0];
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

      // Get company ID for holidays
      let companyId = localStorage.getItem('selectedCompanyId');
      if (!companyId) {
        const token = localStorage.getItem('authToken');
        try {
          const decoded = jwtDecode(token);
          companyId = decoded.companyId || decoded.id;
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }

      // Fetch attendance records and holidays
      const [attendanceRecords, holidayData] = await Promise.all([
        fetchEmployeeAttendance(employeeId, startDate, endDate),
        fetchHolidays(companyId)
      ]);

      // Filter holidays for the selected month
      const monthHolidays = holidayData.filter(holiday => {
        const holidayDate = new Date(holiday.date);
        return holidayDate.getFullYear() === year && holidayDate.getMonth() === monthIndex;
      });

      // Get employee data for joining date and weekly holidays
      const selectedEmployee = employees.find(emp => emp._id === employeeId);
      let joiningDate = null;
      let weeklyHolidays = [];

      if (selectedEmployee) {
        // Get joining date
        if (selectedEmployee.dateOfJoining) {
          joiningDate = new Date(selectedEmployee.dateOfJoining);
        } else if (selectedEmployee.joiningDate) {
          joiningDate = new Date(selectedEmployee.joiningDate);
        }

        // Get weekly holidays from company data
        if (selectedCompany?.weeklyHoliday) {
          weeklyHolidays = Array.isArray(selectedCompany.weeklyHoliday) 
            ? selectedCompany.weeklyHoliday 
            : [selectedCompany.weeklyHoliday];
        } else if (selectedCompany?.weeklyHolidays) {
          weeklyHolidays = Array.isArray(selectedCompany.weeklyHolidays) 
            ? selectedCompany.weeklyHolidays 
            : [selectedCompany.weeklyHolidays];
        }

        // Convert day names to numbers
        weeklyHolidays = weeklyHolidays
          .map(day => {
            if (typeof day === 'string') {
              const dayMap = {
                'sunday': 0, 'sun': 0,
                'monday': 1, 'mon': 1,
                'tuesday': 2, 'tue': 2,
                'wednesday': 3, 'wed': 3,
                'thursday': 4, 'thu': 4,
                'friday': 5, 'fri': 5,
                'saturday': 6, 'sat': 6
              };
              return dayMap[day.toLowerCase()];
            }
            return day;
          })
          .filter(day => day !== undefined);
      }

      // Initialize counters
      let totalWorkingDays = 0;
      let totalHalfDays = 0;
      let totalLeaves = 0;

      // Count attendance records by status (Enhanced logic from DailyAttendance.js)
      attendanceRecords.forEach(record => {
        console.log('📊 Processing attendance record:', {
          date: record.date,
          status: record.status,
          hours: record.hours,
          workingTime: record.workingTime
        });

        switch (record.status) {
          case 'Present':
            totalWorkingDays++;
            console.log('✅ Present day counted as working day');
            break;
          case 'Late':
            totalWorkingDays++;
            console.log('⏰ Late day counted as working day');
            break;
          case 'Half-day':
            totalHalfDays++;
            console.log('🕐 Half-day counted');
            break;
          case 'Absent':
            totalLeaves++;
            console.log('❌ Absent day counted as leave');
            break;
          case 'Incomplete':
          case 'Ongoing':
          case 'Completed':
          default:
            // For other statuses, check work hours to determine category
            let workHours = 0;
            
            console.log(`🔍 Processing record with status: ${record.status}, hours: ${record.hours}, workingTime: ${record.workingTime}`);
            
            // Method 1: Try to get work hours from hours field
            if (record.hours && record.hours !== '--:--' && record.hours !== 'Ongoing' && record.hours !== '-' && record.hours !== '0:00') {
              try {
                if (typeof record.hours === 'string' && record.hours.includes(':')) {
                  const [hours, minutes] = record.hours.split(':').map(Number);
                  if (!isNaN(hours) && !isNaN(minutes)) {
                    workHours = hours + (minutes / 60);
                    console.log(`📊 Parsed from hours field: ${workHours} hours`);
                  }
                } else {
                  // If it's just a number
                  const numericHours = parseFloat(record.hours);
                  if (!isNaN(numericHours) && numericHours > 0) {
                    workHours = numericHours;
                    console.log(`📊 Parsed numeric hours: ${workHours} hours`);
                  }
                }
              } catch (e) {
                console.log('⚠️ Error parsing hours field:', record.hours, e);
              }
            }
            
            // Method 2: Check workingTime field (usually in minutes)
            if (workHours === 0 && record.workingTime && record.workingTime > 0) {
              workHours = record.workingTime / 60; // Convert minutes to hours
              console.log(`📊 Parsed from workingTime: ${record.workingTime} minutes = ${workHours} hours`);
            }
            
            // Method 3: Calculate from inTime and outTime if available
            if (workHours === 0 && record.inTime && record.outTime) {
              try {
                const inTime = new Date(record.inTime);
                const outTime = new Date(record.outTime);
                if (!isNaN(inTime.getTime()) && !isNaN(outTime.getTime())) {
                  const diffMs = outTime - inTime;
                  workHours = diffMs / (1000 * 60 * 60); // Convert to hours
                  console.log(`📊 Calculated from punch times: ${workHours} hours`);
                }
              } catch (e) {
                console.log('⚠️ Error calculating from punch times:', e);
              }
            }
            
            console.log(`🔍 Final calculated work hours: ${workHours} for status: ${record.status}`);
            
            // Enhanced categorization based on work hours and status
            if (workHours >= 8) {
              totalWorkingDays++;
              console.log('✅ Full day working (8+ hours) counted as working day');
            } else if (workHours >= 4) {
              totalHalfDays++;
              console.log('🕐 Half day working (4-8 hours) counted as half day');
            } else if (workHours > 0 && workHours < 4) {
              // Less than 4 hours but some work done - count as half day
              totalHalfDays++;
              console.log('🕐 Partial work (<4 hours) counted as half day');
            } else if (record.status === 'Incomplete') {
              // Incomplete status usually means partial work - count as half day
              totalHalfDays++;
              console.log('🕐 Incomplete status counted as half day');
            } else if (record.status === 'Ongoing') {
              // Ongoing work - count as half day for now
              totalHalfDays++;
              console.log('🕐 Ongoing status counted as half day');
            } else if (record.status === 'Completed' && workHours === 0) {
              // Completed but no hours - assume half day
              totalHalfDays++;
              console.log('🕐 Completed status with no hours counted as half day');
            } else {
              console.log('⚠️ Record not counted - no clear work hours or status');
            }
            break;
        }
      });

      // Count absent days (days without records, excluding holidays and weekends)
      const today = new Date();
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = new Date(year, monthIndex, day);
        const dateStr = currentDate.toISOString().split('T')[0];
        const isPastDate = currentDate < today;
        
        // Check if after joining date
        const isAfterJoining = !joiningDate || currentDate >= joiningDate;
        
        // Check if it's a weekly holiday
        const isWeeklyHoliday = weeklyHolidays.includes(currentDate.getDay());
        
        // Check if it's a company holiday
        const isCompanyHoliday = monthHolidays.some(holiday => holiday.date === dateStr);
        
        // Count as absent if: past date, after joining, not a holiday, and no attendance record
        if (isPastDate && isAfterJoining && !isWeeklyHoliday && !isCompanyHoliday) {
          const hasRecord = attendanceRecords.some(record => record.date === dateStr);
          if (!hasRecord) {
            totalLeaves++;
          }
        }
      }

      console.log('📊 FINAL ATTENDANCE SUMMARY:', {
        totalWorkingDays,
        totalHalfDays,
        totalLeaves,
        totalRecords: attendanceRecords.length,
        monthHolidays: monthHolidays.length,
        weeklyHolidays: weeklyHolidays,
        daysInMonth,
        employeeId,
        payrollMonth,
        breakdown: {
          presentDays: attendanceRecords.filter(r => r.status === 'Present').length,
          lateDays: attendanceRecords.filter(r => r.status === 'Late').length,
          halfDayStatus: attendanceRecords.filter(r => r.status === 'Half-day').length,
          absentDays: attendanceRecords.filter(r => r.status === 'Absent').length,
          incompleteDays: attendanceRecords.filter(r => r.status === 'Incomplete').length,
          ongoingDays: attendanceRecords.filter(r => r.status === 'Ongoing').length,
          completedDays: attendanceRecords.filter(r => r.status === 'Completed').length,
          otherStatuses: attendanceRecords.filter(r => !['Present', 'Late', 'Half-day', 'Absent', 'Incomplete', 'Ongoing', 'Completed'].includes(r.status)).length
        }
      });

      console.log('🎯 HALF DAYS BREAKDOWN:', {
        halfDaysByStatus: attendanceRecords.filter(r => r.status === 'Half-day').length,
        halfDaysByWorkHours: attendanceRecords.filter(r => {
          if (r.status === 'Half-day') return false; // Already counted above
          let hours = 0;
          if (r.hours && r.hours.includes(':')) {
            const [h, m] = r.hours.split(':').map(Number);
            hours = h + (m / 60);
          } else if (r.workingTime) {
            hours = r.workingTime / 60;
          }
          return hours >= 4 && hours < 8;
        }).length,
        partialWorkDays: attendanceRecords.filter(r => {
          if (r.status === 'Half-day') return false;
          let hours = 0;
          if (r.hours && r.hours.includes(':')) {
            const [h, m] = r.hours.split(':').map(Number);
            hours = h + (m / 60);
          } else if (r.workingTime) {
            hours = r.workingTime / 60;
          }
          return hours > 0 && hours < 4;
        }).length,
        incompleteStatusDays: attendanceRecords.filter(r => r.status === 'Incomplete').length,
        ongoingStatusDays: attendanceRecords.filter(r => r.status === 'Ongoing').length
      });

      return { totalWorkingDays, totalHalfDays, totalLeaves };
      
    } catch (error) {
      console.error('Error calculating attendance summary:', error);
      return { totalWorkingDays: 0, totalHalfDays: 0, totalLeaves: 0 };
    } finally {
      setAttendanceLoading(false);
    }
  };

  // Removed fetchPayrolls function since /api/payroll endpoint doesn't exist
  // We'll only use the specific employee/month endpoint when needed


  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: parseFloat(value) || 0
        }
      }));
    } else {
      let newFormData = {
        ...formData,
        [name]: name.includes('total') || name.includes('Days') || name.includes('Leaves') 
          ? parseInt(value) || 0 
          : value
      };

      // Reset employee selection when department changes
      if (name === 'department') {
        newFormData.employee = '';
        console.log('🏢 Department changed, resetting employee selection');
      }
      
      setFormData(newFormData);
      
      // Auto-calculate attendance when both employee and month are selected
      if ((name === 'employee' || name === 'payrollMonth') && 
          newFormData.employee && newFormData.payrollMonth) {
        
        console.log('🎯 Auto-calculating attendance for:', {
          employee: newFormData.employee,
          month: newFormData.payrollMonth
        });
        
        try {
          const attendanceSummary = await calculateAttendanceSummary(
            newFormData.employee, 
            newFormData.payrollMonth
          );
          
          console.log('📊 Calculated attendance summary:', attendanceSummary);
          
          setFormData(prev => ({
            ...prev,
            totalWorkingDays: attendanceSummary.totalWorkingDays,
            totalHalfDays: attendanceSummary.totalHalfDays,
            totalLeaves: attendanceSummary.totalLeaves
          }));
          
          setSuccess(`✅ Attendance calculated: 
          📊 ${attendanceSummary.totalWorkingDays} Working Days 
          🕐 ${attendanceSummary.totalHalfDays} Half Days 
          ❌ ${attendanceSummary.totalLeaves} Leaves`);
          setTimeout(() => setSuccess(''), 5000);
          
        } catch (error) {
          console.error('Error auto-calculating attendance:', error);
          setError('Failed to calculate attendance automatically. Please enter values manually.');
          setTimeout(() => setError(''), 5000);
        }
      }
    }
  };

  const handleGeneratePayroll = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Access token not found. Please login.');
      setIsGenerating(false);
      return;
    }

    // Validate required fields
    if (!formData.employee) {
      setError('Please select an employee.');
      setIsGenerating(false);
      return;
    }

    if (!formData.payrollMonth) {
      setError('Please select a payroll month.');
      setIsGenerating(false);
      return;
    }

    // Get company ID from JWT token
    let companyId;
    try {
      const decoded = jwtDecode(token);
      companyId = decoded.companyId || decoded.company?._id || decoded.orgId || decoded.id;
      console.log('🔍 PAYROLL TOKEN DECODING:', {
        fullToken: decoded,
        extractedCompanyId: companyId
      });
    } catch (error) {
      console.error('❌ Error decoding token for payroll:', error);
      setError('Invalid authentication token. Please login again.');
      setIsGenerating(false);
      return;
    }

    // Fallback to localStorage if not in token
    if (!companyId) {
      companyId = localStorage.getItem('selectedCompanyId');
      console.log('🏢 Using companyId from localStorage:', companyId);
    }

    if (!companyId) {
      setError('Company information not found. Please contact administrator.');
      setIsGenerating(false);
      return;
    }

    // Ensure all numeric values are properly formatted and use correct field names
    const payload = {
      employeeId: formData.employee,  // API expects 'employeeId', not 'employee'
      companyId: companyId,           // API expects 'companyId'
      payrollMonth: formData.payrollMonth,
      totalWorkingDays: parseInt(formData.totalWorkingDays) || 0,
      totalHalfDays: parseInt(formData.totalHalfDays) || 0,
      totalLeaves: parseInt(formData.totalLeaves) || 0,
      deductions: {
        tax: parseFloat(formData.deductions.tax) || 0,
        providentFund: parseFloat(formData.deductions.providentFund) || 0,
        other: parseFloat(formData.deductions.other) || 0,
      },
      incomes: {
        bonus: parseFloat(formData.incomes.bonus) || 0,
        incentives: parseFloat(formData.incomes.incentives) || 0,
        other: parseFloat(formData.incomes.other) || 0,
      },
    };

    console.log('📤 SENDING PAYROLL PAYLOAD:', payload);
    console.log('🎯 Key Fields Check:', {
      employeeId: payload.employeeId,
      companyId: payload.companyId,
      payrollMonth: payload.payrollMonth,
      hasEmployeeId: !!payload.employeeId,
      hasCompanyId: !!payload.companyId,
      hasPayrollMonth: !!payload.payrollMonth
    });

    try {
      const response = await axios.post(
        'https://customersupport-jhy9.onrender.com/api/payroll/generate',
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Payroll generated successfully:', response.data);
      setSuccess('Payroll generated successfully!');
      
      // Refresh payroll history after successful generation
      await fetchPayrollHistory();
      
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Error generating payroll:", error);
      console.error("Error response:", error.response?.data);
      
      // More detailed error handling
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Invalid data provided. Please check all fields.';
        setError(`Validation Error: ${errorMsg}`);
      } else if (error.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        setError('You do not have permission to generate payroll.');
      } else {
        setError(error.response?.data?.message || 'Error generating payroll. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };


  const resetForm = () => {
    setFormData({
      department: '',
      employee: '',
      payrollMonth: '',
      totalWorkingDays: 0,
      totalHalfDays: 0,
      totalLeaves: 0,
      deductions: {
        tax: 0,
        providentFund: 0,
        other: 0,
      },
      incomes: {
        bonus: 0,
        incentives: 0,
        other: 0,
      },
    });
    setShowForm(false);
  };

  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-teal-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payroll data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Generate Salary</h1>
            <p className="text-gray-600 mt-2">Generate and manage employee payroll</p>
          </div>
          <button
            onClick={() => {
              if (showForm) resetForm();
              else setShowForm(true);
            }}
            className="mt-4 md:mt-0 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            {showForm ? 'Cancel' : 'Generate Payroll'}
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-red-700">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-green-700">{success}</p>
            </div>
          </div>
        )}


        {showForm && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Generate New Payroll</h2>
            <form onSubmit={handleGeneratePayroll} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">All Departments</option>
                    {departments.map((department) => (
                      <option key={department._id} value={department._id}>
                        {department.departmentName || department.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Filter employees by department</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                  <select
                    name="employee"
                    value={formData.employee}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Select Employee</option>
                    {filteredEmployees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.teamMemberName || employee.name} - {employee.employeeId}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.department ? 
                      `Showing ${filteredEmployees.length} employees from selected department` : 
                      `Showing all ${filteredEmployees.length} employees`
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payroll Month *</label>
                  <input
                    type="month"
                    name="payrollMonth"
                    value={formData.payrollMonth}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              {/* 🎯 ATTENDANCE CALCULATION SECTION */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Attendance Summary
                  </h3>
                  <div className="flex items-center space-x-3">
                    {formData.employee && formData.payrollMonth && !attendanceLoading && (
                      <button
                        type="button"
                        onClick={async () => {
                          const summary = await calculateAttendanceSummary(formData.employee, formData.payrollMonth);
                          setFormData(prev => ({
                            ...prev,
                            totalWorkingDays: summary.totalWorkingDays,
                            totalHalfDays: summary.totalHalfDays,
                            totalLeaves: summary.totalLeaves
                          }));
                        }}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors duration-200 flex items-center"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Recalculate
                      </button>
                    )}
                    {attendanceLoading && (
                      <div className="flex items-center text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        <span className="text-sm">Calculating...</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 mb-4">
                  📊 Attendance counts are automatically calculated when you select an employee and month. 
                  This includes proper handling of company holidays, weekly holidays, and joining dates.
                  <br />
                  <strong>Half Days include:</strong> Half-day status, 4-8 hours worked, partial work (&lt;4 hours), Incomplete/Ongoing status.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      Total Working Days
                    </label>
                    <input
                      type="number"
                      name="totalWorkingDays"
                      value={formData.totalWorkingDays}
                      onChange={handleInputChange}
                      min="0"
                      disabled={attendanceLoading}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Present + Late days</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
                      Total Half Days
                    </label>
                    <input
                      type="number"
                      name="totalHalfDays"
                      value={formData.totalHalfDays}
                      onChange={handleInputChange}
                      min="0"
                      disabled={attendanceLoading}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">4-8 hours worked</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                      Total Leaves
                    </label>
                    <input
                      type="number"
                      name="totalLeaves"
                      value={formData.totalLeaves}
                      onChange={handleInputChange}
                      min="0"
                      disabled={attendanceLoading}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Excludes holidays</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-4">Deductions</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
                      <input
                        type="number"
                        name="deductions.tax"
                        value={formData.deductions.tax}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Provident Fund</label>
                      <input
                        type="number"
                        name="deductions.providentFund"
                        value={formData.deductions.providentFund}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Other Deductions</label>
                      <input
                        type="number"
                        name="deductions.other"
                        value={formData.deductions.other}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-800 mb-4">Additional Income</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bonus</label>
                      <input
                        type="number"
                        name="incomes.bonus"
                        value={formData.incomes.bonus}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Incentives</label>
                      <input
                        type="number"
                        name="incomes.incentives"
                        value={formData.incomes.incentives}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Other Income</label>
                      <input
                        type="number"
                        name="incomes.other"
                        value={formData.incomes.other}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isGenerating}
                  className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating Payroll...
                    </div>
                  ) : (
                    'Generate Payroll'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Payroll History</h2>
            <button
              onClick={fetchPayrollHistory}
              disabled={historyLoading}
              className="px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors duration-200 flex items-center disabled:opacity-50"
            >
              {historyLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>

          {historyLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading payroll history...</p>
            </div>
          ) : payrollHistory.length === 0 ? (
            <div className="p-12 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-4 text-gray-600">No payroll records found.</p>
              <p className="text-sm text-gray-500">Generate your first payroll to see it here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attendance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gross Salary
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Deductions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net Salary
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Generated On
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payrollHistory.map((payroll, index) => (
                    <tr key={payroll._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-400 to-teal-500 flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {(payroll.employee?.teamMemberName || payroll.employee?.name || 'N/A').charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {payroll.employee?.teamMemberName || payroll.employee?.name || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {payroll.employee?.employeeId || payroll.employeeId || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {payroll.employee?.department?.departmentName || 
                           payroll.employee?.department?.name || 
                           payroll.department || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-medium">
                          {payroll.payrollMonth ? new Date(payroll.payrollMonth + '-01').toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long' 
                          }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs space-y-1">
                          <div className="flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            <span className="text-gray-600">Working: {payroll.totalWorkingDays || 0}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                            <span className="text-gray-600">Half: {payroll.totalHalfDays || 0}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                            <span className="text-gray-600">Leaves: {payroll.totalLeaves || 0}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{payroll.grossSalary ? payroll.grossSalary.toLocaleString('en-IN') : '0'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-red-600">
                          -₹{payroll.totalDeductions ? payroll.totalDeductions.toLocaleString('en-IN') : '0'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-green-600">
                          ₹{payroll.netSalary ? payroll.netSalary.toLocaleString('en-IN') : '0'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {payroll.createdAt ? new Date(payroll.createdAt).toLocaleDateString('en-IN') : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {payroll.createdAt ? new Date(payroll.createdAt).toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          }) : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          payroll.status === 'Paid' 
                            ? 'bg-green-100 text-green-800' 
                            : payroll.status === 'Pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {payroll.status || 'Generated'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Summary Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Total Records: {payrollHistory.length}</span>
                  <span>
                    Total Net Salary: ₹{payrollHistory.reduce((sum, payroll) => sum + (payroll.netSalary || 0), 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateSalary;
